import { memo, useMemo } from "react";
import { cellKey } from "./utils";

function CrosswordGridInner({ cw, grid, sel, activeCells, wrongKeys, cellPx, cellRefs, gridAreaRef, onSelect }) {
  const cellsByKey = useMemo(() => {
    const m = {};
    cw.cells.forEach((c) => { m[cellKey(c.row, c.col)] = c; });
    return m;
  }, [cw]);

  const cells = useMemo(
    () =>
      Array.from({ length: cw.rows }).flatMap((_, r) =>
        Array.from({ length: cw.cols }).map((__, c) => {
          const key = cellKey(r, c);
          const cell = cellsByKey[key];
          const sz = { width: cellPx, height: cellPx };
          if (!cell) return <div key={key} style={sz} aria-hidden="true" />;
          if (cell.block) return <div key={key} style={sz} className="bg-black rounded-sm" data-testid={`crossword-block-${r}-${c}`} />;
          const value = grid[key] || "";
          const isSel = sel && sel.r === r && sel.c === c;
          const inWord = activeCells.has(key);
          const isWrong = wrongKeys.has(key) && !!value;
          return (
            <div
              key={key}
              ref={(el) => { cellRefs.current[key] = el; }}
              onClick={() => onSelect(r, c)}
              role="button"
              aria-invalid={isWrong || undefined}
              aria-label={`Row ${r + 1} column ${c + 1}${cell.number ? `, clue ${cell.number}` : ""}${value ? `, letter ${value}` : ", empty"}${isWrong ? ", incorrect letter" : ""}`}
              className="relative border rounded-sm font-display uppercase select-none flex items-center justify-center"
              style={{
                ...sz,
                cursor: "pointer",
                fontSize: Math.max(13, Math.floor(cellPx * 0.52)),
                color: isWrong ? "#dc2626" : "#000",
                textDecoration: isWrong ? "underline wavy #dc2626 1px" : "none",
                borderColor: isSel ? "var(--motif-primary)" : isWrong ? "#dc2626" : "rgba(0,0,0,0.4)",
                borderWidth: isSel ? 2 : 1,
                background: isSel ? "var(--motif-accent)" : inWord ? "rgba(212,178,128,0.28)" : "#fff",
              }}
              data-testid={`crossword-cell-${r}-${c}`}
            >
              {cell.number != null && (
                <span className="absolute top-0 left-0.5 text-[8px] leading-none pt-px text-gray-500 pointer-events-none">{cell.number}</span>
              )}
              {value}
            </div>
          );
        })
      ),
    [cw, cellsByKey, grid, sel, activeCells, wrongKeys, cellPx, cellRefs, onSelect]
  );

  return (
    <div ref={gridAreaRef} className="flex-1 min-h-0 overflow-auto p-2" data-testid="crossword-grid-scroller">
      <div
        className="grid gap-[2px] m-auto w-max"
        style={{ gridTemplateColumns: `repeat(${cw.cols}, ${cellPx}px)` }}
        data-testid="crossword-grid"
      >
        {cells}
      </div>
    </div>
  );
}

export const CrosswordGrid = memo(CrosswordGridInner);
